import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useICalConflicts } from '../../hooks/useICalConflicts';
import { ConflictStatus, ICalConflict } from '../../types/icalConflict';
import { ConflictListItem } from './ConflictListItem';
import { ConflictResolveDialog } from './ConflictResolveDialog';

interface ConflictsPanelProps {
  readonly apartmentId: string;
}

/**
 * Apartment-scoped panel listing every active/resolved iCal conflict (US1 MVP). Distinguishes
 * `DOUBLE_BOOKING` from `NO_AVAILABILITY` visually and by content, supports an active/resolved
 * toggle (FR-004), and renders explicit empty / unavailable states rather than crashing — covering
 * the "not yet live" network caveat from `contracts/ical-conflicts-api.md`.
 */
export const ConflictsPanel = ({ apartmentId }: ConflictsPanelProps) => {
  const { t } = useTranslation(['pages']);
  const [statusFilter, setStatusFilter] = useState<ConflictStatus>('ACTIVE');
  const [resolvingConflict, setResolvingConflict] = useState<ICalConflict | null>(null);

  const { data: conflicts, isLoading, isError } = useICalConflicts(apartmentId, statusFilter);

  return (
    <div className="space-y-4">
      <div className="flex bg-gray-100 rounded-lg p-1 w-fit" role="tablist">
        <button
          role="tab"
          aria-selected={statusFilter === 'ACTIVE'}
          onClick={() => setStatusFilter('ACTIVE')}
          className={`px-4 py-2 rounded-md transition-colors ${
            statusFilter === 'ACTIVE'
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('pages:conflictsFilterActive')}
        </button>
        <button
          role="tab"
          aria-selected={statusFilter === 'RESOLVED'}
          onClick={() => setStatusFilter('RESOLVED')}
          className={`px-4 py-2 rounded-md transition-colors ${
            statusFilter === 'RESOLVED'
              ? 'bg-white text-primary-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {t('pages:conflictsFilterResolved')}
        </button>
      </div>

      {isLoading ? (
        <div className="card">
          <p className="text-gray-600 text-center py-12">{t('pages:conflictsLoading')}</p>
        </div>
      ) : isError ? (
        <div className="card" data-testid="conflicts-unavailable">
          <div className="text-center py-12">
            <AlertCircle size={48} className="text-amber-500 mx-auto mb-4" />
            <p className="text-gray-600">{t('pages:conflictsUnavailable')}</p>
          </div>
        </div>
      ) : !conflicts || conflicts.length === 0 ? (
        <div className="card" data-testid="conflicts-empty-state">
          <div className="text-center py-12">
            <CheckCircle2 size={48} className="text-green-500 mx-auto mb-4" />
            <p className="text-gray-600">
              {statusFilter === 'ACTIVE'
                ? t('pages:conflictsEmptyActive')
                : t('pages:conflictsEmptyResolved')}
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {conflicts.map(conflict => (
            <ConflictListItem
              key={conflict.conflictId}
              conflict={conflict}
              onResolve={setResolvingConflict}
            />
          ))}
        </div>
      )}

      {resolvingConflict && (
        <ConflictResolveDialog
          conflict={resolvingConflict}
          onClose={() => setResolvingConflict(null)}
          onResolved={() => setResolvingConflict(null)}
        />
      )}
    </div>
  );
};
